// A synchronous "does this browser hold a signed-in session?" hint (ADR-WBC1).
// It reads the entry supabase-js persists, without loading supabase-js, so the
// first screen can be chosen before the bundle has run. It is a hint only: it
// never authorizes anything, and the app still waits for Supabase's own
// answer before the console renders. Dependency-free: the head script bundles
// this file.

/** The key the e2e auth stub stores its session under (src/lib/supabase.ts). */
export const E2E_SESSION_KEY = "tinkr.e2e.session";

/**
 * The localStorage key supabase-js uses by default for a project URL:
 * `sb-<first label of the hostname>-auth-token` (SupabaseClient constructor).
 * Null when the URL is missing or not http(s), as `hasSupabaseConfig` treats it.
 */
export function supabaseSessionStorageKey(supabaseUrl: string | undefined | null): string | null {
  const trimmed = supabaseUrl?.trim();
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) return null;
  try {
    const url = new URL(trimmed.endsWith("/") ? trimmed : `${trimmed}/`);
    return `sb-${url.hostname.split(".")[0]}-auth-token`;
  } catch {
    return null;
  }
}

/** The key holding this build's session, or null when the build has no auth. */
export function sessionStorageKeyFor(env: {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_PUBLISHABLE_KEY?: string;
  VITE_E2E_AUTH_STUB?: string;
}): string | null {
  if (env.VITE_E2E_AUTH_STUB === "true") return E2E_SESSION_KEY;
  if (!env.VITE_SUPABASE_PUBLISHABLE_KEY) return null;
  return supabaseSessionStorageKey(env.VITE_SUPABASE_URL);
}

/** The stored session's shape, as far as the hint needs it. */
export interface StoredSessionHint {
  userId: string | null;
  roles: unknown;
}

/**
 * Parses the stored entry. A session needs a refresh token to survive (an
 * expired access token is refreshed on load); anything else is no session.
 * Never throws: storage can be blocked, and the entry can be anything.
 */
export function readStoredSession(storage: Pick<Storage, "getItem"> | null | undefined, key: string | null): StoredSessionHint | null {
  if (!storage || !key) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const session = parsed as { refresh_token?: unknown; user?: { id?: unknown; app_metadata?: { roles?: unknown } } };
    if (typeof session.refresh_token !== "string" || !session.refresh_token) return null;
    const userId = typeof session.user?.id === "string" ? session.user.id : null;
    return { userId, roles: session.user?.app_metadata?.roles };
  } catch {
    return null;
  }
}

function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The running app's hint; callers pass `import.meta.env` (the build's own config). */
export function hasStoredSession(env: Parameters<typeof sessionStorageKeyFor>[0]): boolean {
  return readStoredSession(browserStorage(), sessionStorageKeyFor(env)) !== null;
}
